import React, { useContext, useEffect, useState } from 'react';
import { CSVDownload } from 'react-csv';
import moment from 'moment-timezone';
import queryString from 'query-string';
import { useHistory } from 'react-router-dom';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Switch from '@material-ui/core/Switch';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles } from '@material-ui/core/styles';
import { Context as OrdersContext } from '../../context/OrdersContext';
import { Context as OffersContext } from '../../context/OffersContext';
import { Context as AnalyticsContext } from '../../context/AnalyticsContext';
import { Context as AffIdsContext } from '../../context/AffIdsContext';
import Error from '../../components/Error/Error';
import DatePicker from '../../components/DatePicker/DatePicker';
import CustomFilters from '../../components/CustomFilters/CustomFilters';
import MultiSelect from '../../components/MultiSelect/MultiSelect';
import OrdersTable from './OrdersTable';
import Analytics from './Analytics';
import { CircularProgress, IconButton, LinearProgress } from '@material-ui/core';
import { api } from '../../api/api';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column'
  },
  filtersSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1)
  },
  ordersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    flex: 1
  },
  formControl: {
    minWidth: 250,
    [theme.breakpoints.down('md')]: {
      minWidth: 200
    }
  },
  checkbox: {
    marginTop: 0
  },
  searchByOrder: {
    alignSelf: 'flex-end'
  },
  apply: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1)
  }
}));

const orderStatuses = ['Completed', 'Not completed', 'Processed', 'Not processed', 'Successful', 'Declined'];

export default function HomePage({ onRender }) {
  const classes = useStyles();
  const history = useHistory();
  // eslint-disable-next-line no-restricted-globals
  const parsedQuery = queryString.parse(location.search);

  // Get timezone from query params or default to LA
  const defaultTimezone = parsedQuery.timezone || 'America/Los_Angeles';

  // Initialize dates in the selected timezone (start at 00:00:00, end at 23:59:59)
  const initStartDate = moment.tz(defaultTimezone).startOf('day').toISOString();
  const initEndDate = moment.tz(defaultTimezone).endOf('day').toISOString();

  const [exportReady, setExportReady] = useState(false);
  const [exportingOrders, setExportingOrders] = useState(false);
  const [exportedOrders, setExportedOrders] = useState([]);
  const [applyButtonVisible, setApplyButtonVisible] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 25
  });

  // Timezone selector state with LA as default
  const [timezone, setTimezone] = useState(defaultTimezone);

  const [selectedRange, setSelectedRange] = useState(parsedQuery.range || 'Today');

  const [filters, setFilters] = useState({
    startDate: parsedQuery.startDate || initStartDate,
    endDate: parsedQuery.endDate || initEndDate,
    offer: parsedQuery.offer ? parsedQuery.offer.split(',') : [], // Array of offer names
    affId: parsedQuery.affId ? parsedQuery.affId.split(',') : [],
    status: '',
    noDuplicates: false,
    // Initialize custom filters from URL query parameters
    customFilters: Object.entries(parsedQuery).reduce((customFilters, [key, value]) => {
      // Skip standard filter parameters and keep only custom ones
      if (
        !['startDate', 'endDate', 'range', 'offer', 'affId', 'status', 'noDuplicates', 'timezone'].includes(
          key
        )
      ) {
        customFilters[key] = value;
      }
      return customFilters;
    }, {})
  });

  // Context
  const {
    state: { orders, loading: ordersLoading, error: ordersError },
    loadOrders,
    deleteOrder
  } = useContext(OrdersContext);

  const {
    state: { offers, error: offersError },
    loadOffers
  } = useContext(OffersContext);

  const {
    state: { affIds, error: affIdsError },
    loadAffIds
  } = useContext(AffIdsContext);

  const {
    state: { analytics, loading: analyticsLoading, error: analyticsError },
    loadAnalytics
  } = useContext(AnalyticsContext);

  // Effects
  useEffect(() => {
    onRender('Orders');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  useEffect(() => {
    loadOffers();

    // Create API filters that include any custom filters from URL
    const apiFilters = {
      ...filters,
      timezone,
      // Convert offer array to comma-separated string for API
      offer: filters.offer.length > 0 ? filters.offer.join(',') : undefined,
      // Convert affId array to comma-separated string for API
      affId: filters.affId.length > 0 ? filters.affId.join(',') : undefined
    };
    const analyticsFilters = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      offer: filters.offer.length > 0 ? filters.offer.join(',') : undefined,
      affId: filters.affId.length > 0 ? filters.affId.join(',') : undefined,
      status: filters.status,
      noDuplicates: filters.noDuplicates,
      timezone
    };

    // Spread customFilters from URL into the main object for orders API
    if (Object.keys(filters.customFilters).length > 0) {
      Object.entries(filters.customFilters).forEach(([key, value]) => {
        apiFilters[key] = value;
      });

      // Remove the customFilters object itself
      delete apiFilters.customFilters;
    }

    // Load data with filters (including any from URL)
    loadAffIds(analyticsFilters);
    loadAnalytics(analyticsFilters);
    loadOrders({
      ...pagination,
      ...apiFilters
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function exportOrders() {
    setExportReady(false);
    setExportingOrders(true);

    try {
      // Create API filters including custom filters
      const apiFilters = {
        ...filters,
        timezone,
        // Convert offer array to comma-separated string for API
        offer: filters.offer.length > 0 ? filters.offer.join(',') : undefined,
        // Convert affId array to comma-separated string for API
        affId: filters.affId.length > 0 ? filters.affId.join(',') : undefined
      };

      // Spread customFilters into the main object for the API request
      if (Object.keys(filters.customFilters).length > 0) {
        Object.entries(filters.customFilters).forEach(([key, value]) => {
          apiFilters[key] = value;
        });
      }

      // Remove the customFilters object itself as we've already spread its contents
      delete apiFilters.customFilters;

      const response = await api.get('/exportOrders', {
        params: apiFilters
      });

      setExportedOrders(response.data);
      setExportingOrders(false);
      setExportReady(true);
    } catch (error) {
      setExportingOrders(false);
      setExportReady(false);
    }
  }

  function updateQueryParams() {
    // Start with standard parameters
    const queryParams = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      range: selectedRange,
      timezone: timezone,
      offer: filters.offer.length > 0 ? filters.offer.join(',') : undefined, // Join array to comma-separated string
      affId: filters.affId.length > 0 ? filters.affId.join(',') : undefined,
      status: filters.status || undefined,
      noDuplicates: filters.noDuplicates ? 'true' : undefined
    };

    // Add custom filters to the URL
    if (Object.keys(filters.customFilters).length > 0) {
      Object.entries(filters.customFilters).forEach(([key, value]) => {
        if (value) {
          // Only add if value is not empty
          queryParams[key] = value;
        }
      });
    }

    // Clean up undefined properties
    Object.keys(queryParams).forEach((key) => queryParams[key] === undefined && delete queryParams[key]);

    const searchString = queryString.stringify(queryParams);

    history.push({
      search: searchString
    });
  }

  function handlePaginationChange(newPagination) {
    setPagination(newPagination);

    // Construct apiFilters including custom filters for the API call
    const apiFilters = {
      ...filters,
      timezone,
      offer: filters.offer.length > 0 ? filters.offer.join(',') : undefined,
      affId: filters.affId.length > 0 ? filters.affId.join(',') : undefined
    };
    if (Object.keys(filters.customFilters).length > 0) {
      Object.entries(filters.customFilters).forEach(([key, value]) => {
        apiFilters[key] = value;
      });
    }
    delete apiFilters.customFilters; // Remove the nested object after spreading

    loadOrders({
      ...newPagination,
      ...apiFilters // Pass the combined filters
    });
  }

  function handleFilterChange(name, value) {
    const newFilters = { ...filters, [name]: value };
    const newPagination = {
      page: 0,
      limit: 25
    };

    setFilters(newFilters);
    setPagination(newPagination);
    setApplyButtonVisible(true);
  }

  function applyFilters() {
    updateQueryParams();

    // Create API filters from the current filters
    const apiFilters = {
      ...filters,
      timezone,
      // Convert offer array to comma-separated string for API
      offer: filters.offer.length > 0 ? filters.offer.join(',') : undefined,
      // Convert affId array to comma-separated string for API
      affId: filters.affId.length > 0 ? filters.affId.join(',') : undefined
    };
    const analyticsFilters = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      offer: filters.offer.length > 0 ? filters.offer.join(',') : undefined,
      affId: filters.affId.length > 0 ? filters.affId.join(',') : undefined,
      status: filters.status,
      noDuplicates: filters.noDuplicates,
      timezone
    };

    // Spread customFilters into the main object for orders API request only
    if (Object.keys(filters.customFilters).length > 0) {
      Object.entries(filters.customFilters).forEach(([key, value]) => {
        apiFilters[key] = value;
      });
    }

    // Remove the customFilters object itself as we've already spread its contents
    delete apiFilters.customFilters;

    // Load analytics with standard filters only
    loadAnalytics(analyticsFilters);

    // Load orders with all filters (including custom ones)
    loadOrders({
      ...pagination,
      ...apiFilters
    });

    // Load affIds with standard filters only
    loadAffIds(analyticsFilters);

    setApplyButtonVisible(false);
  }

  function isFilterApplied() {
    return (
      !moment(filters.startDate).isSame(initStartDate, 'day') ||
      !moment(filters.endDate).isSame(initEndDate, 'day') ||
      filters.offer.length > 0 ||
      filters.affId.length > 0 ||
      filters.status !== '' ||
      filters.noDuplicates !== false ||
      Object.keys(filters.customFilters).length > 0
    );
  }

  function resetFilters() {
    const resetTimezone = 'America/Los_Angeles';
    const resetStartDate = moment.tz(resetTimezone).startOf('day').toISOString();
    const resetEndDate = moment.tz(resetTimezone).endOf('day').toISOString();

    const initialFilters = {
      startDate: resetStartDate,
      endDate: resetEndDate,
      offer: [],
      affId: [],
      status: '',
      noDuplicates: false,
      customFilters: {}
    };

    setFilters(initialFilters);
    setPagination({ page: 0, limit: 25 });
    setSelectedRange('Today');
    setTimezone(resetTimezone);

    history.push({
      search: '' // Clear the query string in the URL
    });

    const analyticsFilters = {
      ...initialFilters,
      timezone: resetTimezone
    };

    loadAnalytics(analyticsFilters);
    loadOrders({
      ...initialFilters,
      page: 0,
      limit: 25,
      timezone: resetTimezone
    });
    loadAffIds(analyticsFilters);

    setApplyButtonVisible(false);
  }

  function renderOfferSelect() {
    function handleChange(selectedOffers) {
      // selectedOffers is an array of { id, name } objects
      const offerIds = selectedOffers ? selectedOffers.map((offer) => offer.id) : [];
      handleFilterChange('offer', offerIds);
    }

    const selectedOffers = filters.offer
      .map((offerId) => {
        const found = offers.find((o) => o._id === offerId);
        return found ? { id: found._id, name: found.name } : null;
      })
      .filter(Boolean);

    return (
      <MultiSelect
        className={classes.formControl}
        label='Offers'
        optionLabel='name'
        options={offers.map((o) => ({ id: o._id, name: o.name }))}
        value={selectedOffers}
        onChange={handleChange}
      />
    );
  }

  function renderAffSelect() {
    function handleChange(selectedAffIds) {
      const ids = selectedAffIds ? selectedAffIds.map((item) => item.id) : [];
      handleFilterChange('affId', ids);
    }

    const selectedAffIds = filters.affId
      .map((id) => (affIds.includes(id) ? { id, name: id } : null))
      .filter(Boolean);

    return (
      <MultiSelect
        className={classes.formControl}
        label='Affiliates'
        optionLabel='name'
        options={affIds.map((id) => ({ id, name: id }))}
        value={selectedAffIds}
        onChange={handleChange}
      />
    );
  }

  function renderStatusSelect() {
    function handleChange(e) {
      handleFilterChange('status', e.target.value);
    }

    return (
      <FormControl className={classes.formControl}>
        <InputLabel id='status' style={{ backgroundColor: '#fafafa' }}>
          Order status
        </InputLabel>
        <Select labelId='status' value={filters.status} onChange={handleChange}>
          <MenuItem value=''>All</MenuItem>
          {orderStatuses.map((item, index) => (
            <MenuItem key={index} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  function renderTimezoneSelect() {
    const timezones = [
      { value: 'America/Los_Angeles', label: 'Pacific (PST/PDT)' },
      { value: 'America/Denver', label: 'Mountain (MST/MDT)' },
      { value: 'America/Chicago', label: 'Central (CST/CDT)' },
      { value: 'America/New_York', label: 'Eastern (EST/EDT)' },
      { value: 'America/Anchorage', label: 'Alaska (AKST/AKDT)' },
      { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' }
    ];

    function handleChange(e) {
      const newTimezone = e.target.value;
      setTimezone(newTimezone);

      // Recalculate dates in the new timezone based on current range
      if (selectedRange !== 'Custom') {
        let startDate, endDate;

        switch (selectedRange) {
          case 'Today':
            startDate = moment.tz(newTimezone).startOf('day').toISOString();
            endDate = moment.tz(newTimezone).endOf('day').toISOString();
            break;
          case 'Yesterday':
            startDate = moment.tz(newTimezone).subtract(1, 'day').startOf('day').toISOString();
            endDate = moment.tz(newTimezone).subtract(1, 'day').endOf('day').toISOString();
            break;
          case 'Last 7 days':
            startDate = moment.tz(newTimezone).subtract(6, 'days').startOf('day').toISOString();
            endDate = moment.tz(newTimezone).endOf('day').toISOString();
            break;
          case 'Last 30 days':
            startDate = moment.tz(newTimezone).subtract(29, 'days').startOf('day').toISOString();
            endDate = moment.tz(newTimezone).endOf('day').toISOString();
            break;
          case 'This month':
            startDate = moment.tz(newTimezone).startOf('month').toISOString();
            endDate = moment.tz(newTimezone).endOf('month').toISOString();
            break;
          case 'Last month':
            startDate = moment.tz(newTimezone).subtract(1, 'month').startOf('month').toISOString();
            endDate = moment.tz(newTimezone).subtract(1, 'month').endOf('month').toISOString();
            break;
          default:
            break;
        }

        if (startDate && endDate) {
          setFilters({
            ...filters,
            startDate,
            endDate
          });
        }
      }

      setApplyButtonVisible(true);
    }

    return (
      <FormControl className={classes.formControl}>
        <InputLabel id='timezone' style={{ backgroundColor: '#fafafa' }}>
          Timezone
        </InputLabel>
        <Select labelId='timezone' value={timezone} onChange={handleChange}>
          {timezones.map((tz, index) => (
            <MenuItem key={index} value={tz.value}>
              {tz.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  function renderDuplicatesFilter() {
    function handleChange(e) {
      handleFilterChange('noDuplicates', e.target.checked);
    }

    return (
      <FormControlLabel
        control={
          <Switch
            checked={filters.noDuplicates}
            onChange={handleChange}
            name='duplicates-filter'
            color='primary'
          />
        }
        label='Filter duplicates'
      />
    );
  }

  function renderRangeSelector() {
    const ranges = [
      'Today',
      'Yesterday',
      'Last 7 days',
      'Last 30 days',
      'This month',
      'Last month',
      'Custom'
    ];

    function handleChange(e) {
      setSelectedRange(e.target.value);

      let startDate, endDate;

      switch (e.target.value) {
        case 'Today':
          startDate = moment.tz(timezone).startOf('day').toISOString();
          endDate = moment.tz(timezone).endOf('day').toISOString();
          break;
        case 'Yesterday':
          startDate = moment.tz(timezone).subtract(1, 'day').startOf('day').toISOString();
          endDate = moment.tz(timezone).subtract(1, 'day').endOf('day').toISOString();
          break;
        case 'Last 7 days':
          startDate = moment.tz(timezone).subtract(6, 'days').startOf('day').toISOString();
          endDate = moment.tz(timezone).endOf('day').toISOString();
          break;
        case 'Last 30 days':
          startDate = moment.tz(timezone).subtract(29, 'days').startOf('day').toISOString();
          endDate = moment.tz(timezone).endOf('day').toISOString();
          break;
        case 'This month':
          startDate = moment.tz(timezone).startOf('month').toISOString();
          endDate = moment.tz(timezone).endOf('month').toISOString();
          break;
        case 'Last month':
          startDate = moment.tz(timezone).subtract(1, 'month').startOf('month').toISOString();
          endDate = moment.tz(timezone).subtract(1, 'month').endOf('month').toISOString();
          break;
        case 'Custom':
          startDate = initStartDate;
          endDate = initEndDate;
          break;
        default:
          break;
      }

      setFilters({
        ...filters,
        startDate,
        endDate
      });

      const newPagination = {
        page: 0,
        limit: 25
      };
      setPagination(newPagination);
      setApplyButtonVisible(true);
    }

    return (
      <FormControl className={classes.formControl}>
        <InputLabel id='range' style={{ backgroundColor: '#fafafa' }}>
          Date Range
        </InputLabel>
        <Select labelId='range' value={selectedRange} onChange={handleChange}>
          {ranges.map((item, index) => (
            <MenuItem key={index} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  function renderPageContent() {
    if (ordersError || offersError || analyticsError || affIdsError) {
      return <Error error={ordersError || offersError || analyticsError || affIdsError} />;
    }

    if (ordersLoading) {
      return <LinearProgress />;
    }

    return (
      <Box>
        <Analytics data={analytics} loading={analyticsLoading} />
        <OrdersTable
          pagination={pagination}
          ordersTotal={analytics.ordersTotal}
          onPaginationChange={handlePaginationChange}
          orders={orders}
          onDeleteOrder={deleteOrder}
          timezone={timezone}
        />
      </Box>
    );
  }

  return (
    <Container className={classes.container} maxWidth={false}>
      <Box className={classes.filtersSection}>
        <Box className={classes.filtersRow}>
          {renderRangeSelector()}
          {renderTimezoneSelect()}
          {selectedRange === 'Custom' && (
            <DatePicker
              maxDate={initEndDate}
              startDate={filters.startDate}
              endDate={filters.endDate}
              onStartDateChange={(date) => {
                const tzDate = moment(date).tz(timezone, true).startOf('day').toISOString();
                handleFilterChange('startDate', tzDate);
              }}
              onEndDateChange={(date) => {
                const tzDate = moment(date).tz(timezone, true).endOf('day').toISOString();
                handleFilterChange('endDate', tzDate);
              }}
            />
          )}
        </Box>
        <Box className={classes.filtersRow}>
          {renderOfferSelect()}
          {renderAffSelect()}
        </Box>
        <Box className={classes.filtersRow}>
          {renderDuplicatesFilter()}
          {renderStatusSelect()}
        </Box>
        <Box className={classes.filtersRow}>
          <CustomFilters onFilterChange={handleFilterChange} customFilters={filters.customFilters} />
        </Box>
        <Box className={classes.apply}>
          {applyButtonVisible && (
            <Button
              variant='contained'
              color='primary'
              style={{ width: '200px' }}
              onClick={() => applyFilters()}
            >
              Apply
            </Button>
          )}
          {isFilterApplied() && (
            <Button
              variant='outlined'
              color='secondary'
              size='small'
              style={{ padding: '5px 10px', minWidth: '100px' }}
              onClick={() => resetFilters()}
            >
              Reset
            </Button>
          )}
        </Box>
      </Box>
      <Box className={classes.ordersHeader}>
        <Typography variant='h6' component='h5' className={classes.title}>
          Orders ({analytics.ordersTotal})
          <IconButton
            disabled={true || exportingOrders}
            variant='contained'
            color='primary'
            component='span'
            onClick={() => exportOrders()}
          >
            {exportingOrders ? <CircularProgress size={20} /> : <SaveAltIcon />}
          </IconButton>
          {exportReady && <CSVDownload data={exportedOrders}></CSVDownload>}
        </Typography>
      </Box>
      {renderPageContent()}
    </Container>
  );
}
